import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Badge } from "@/components/common/DataTable";
import { opportunityService, salesActivityService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { STAGES, STAGE_COLOR, fmtMoney } from "./OpportunitiesPage";
import { ArrowLeft, Loader2, PhoneCall } from "lucide-react";

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const o = await opportunityService.get(id);
      setOpp(o);
      const a = await salesActivityService.list({ relatedType: "Opportunity", relatedId: id });
      setActivities(a.items || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!opp) return <p className="text-muted-foreground py-10 text-center">Opportunity not found</p>;

  const canStage = hasPermission("OPPORTUNITY.MANAGE_STAGE");

  const move = async (stage) => {
    try { const u = await opportunityService.moveStage(id, stage); setOpp(u); toast.success(`Moved to ${stage}`); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="opportunity-detail-page">
      <button onClick={() => navigate("/admin/opportunities")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="opp-back">
        <ArrowLeft className="h-4 w-4" /> Back to Opportunities
      </button>
      <PageHeader title={opp.name} subtitle={`${opp.opportunityCode} · ${opp.customerName || "—"}`} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card rounded-xl border border-border p-6 grid sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="opp-overview">
            <Row label="Amount" value={fmtMoney(opp.amount)} />
            <Row label="Probability" value={`${opp.probability ?? 0}%`} />
            <Row label="Stage" value={<Badge color={STAGE_COLOR[opp.stage] || "slate"}>{opp.stage}</Badge>} />
            <Row label="Status" value={<Badge color={opp.status === "Won" ? "green" : opp.status === "Lost" ? "red" : "blue"}>{opp.status}</Badge>} />
            <Row label="Expected Close" value={opp.expectedCloseDate || "—"} />
            <Row label="Weighted Value" value={fmtMoney((opp.amount || 0) * (opp.probability || 0) / 100)} />
            <div className="sm:col-span-2"><Row label="Description" value={opp.description} /></div>
          </div>

          <div className="bg-card rounded-xl border border-border p-5" data-testid="opp-activities">
            <h3 className="font-semibold text-foreground mb-4">Sales Activities</h3>
            {activities.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No activities logged for this opportunity</p> : (
              <div className="space-y-2">
                {activities.map((a) => (
                  <div key={a.id} className="flex gap-3 border-b border-border pb-2" data-testid={`opp-activity-${a.id}`}>
                    <PhoneCall className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <div><p className="text-sm font-medium text-foreground">{a.subject}</p><p className="text-xs text-muted-foreground">{a.type} · {a.status}</p></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-5 h-fit" data-testid="opp-stage-panel">
          <h3 className="font-semibold text-foreground mb-4">Move Stage</h3>
          <div className="space-y-2">
            {STAGES.map((s) => (
              <button key={s} onClick={() => canStage && move(s)} disabled={!canStage || s === opp.stage}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm border transition-colors ${s === opp.stage ? "bg-primary/10 border-primary text-primary font-semibold" : "border-border hover:bg-muted text-foreground disabled:opacity-50"}`}
                data-testid={`opp-stage-${s.replace(/\s/g, "-")}`}>
                {s}
              </button>
            ))}
          </div>
          {!canStage && <p className="text-xs text-muted-foreground mt-3">You don't have permission to change stages.</p>}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-0.5">{label}</p>
      <div className="text-sm text-foreground">{value || "—"}</div>
    </div>
  );
}
